import {Integer} from 'platform/foundation';

import {CurrencyCodeType} from 'shared/web';

import {RendererRegistry} from './RendererRegistry';

export type GetSchemaApiResponse = /** status 200  */ SchemaResponseBody;
export type GetSchemaApiArg = {
  id: string;
};

export type SchemaResponseBody = {
  root: CardRendererResponseBody | RowRendererResponseBody;
};

export type OptionResponseBody = {
  value: string;
  label: string;
  isDisabled?: boolean;
};
export type DependentOptionResponseBody = {
  name: string;
  action: 'lt' | 'lte' | 'gt' | 'gte';
};
export type EventActionResponseBody = {
  field: string;
  action: 'reset';
};
export type ShowWhenResponseBody = {
  field: string;
  isEqualTo: string | boolean | number;
};

export type InputRuleSet = [
  {value: boolean; message: string; type: 'required'},
  {value: number; message: string; type: 'minLength'},
  {value: number; message: string; type: 'maxLength'},
  {value: number; message: string; type: 'min'},
  {value: number; message: string; type: 'max'},
];

export type CheckboxesRendererResponseBody = {
  isReadOnly?: boolean;
  renderer: 'checkboxes';
  name: string;
  url: string | null;
  tooltip?: string | null;
  label: string;
  columnSpan: Integer | null;
  rules?: InputRuleSet | null;
  defaultOptions: OptionResponseBody[];
  dependentOptions: DependentOptionResponseBody[] | null;
  onChange: EventActionResponseBody[] | null;
  onBlur: EventActionResponseBody[] | null;
  onFocus: EventActionResponseBody[] | null;
  showWhen: ShowWhenResponseBody | null;
};
export type CheckboxRendererResponseBody = {
  isReadOnly?: boolean;
  renderer: 'checkbox';
  name: string;
  label: string;
  columnSpan: Integer | null;
  tooltip?: string | null;
  rules?: InputRuleSet | null;
  onChange: EventActionResponseBody[] | null;
  onBlur: EventActionResponseBody[] | null;
  onFocus: EventActionResponseBody[] | null;
  showWhen: ShowWhenResponseBody | null;
};
export type ChipsRendererResponseBody = {
  isReadOnly?: boolean;
  renderer: 'chips';
  name: string;
  label: string;
  columnSpan: Integer | null;
  url: string | null;
  tooltip?: string | null;
  rules?: InputRuleSet | null;
  defaultOptions: OptionResponseBody[];
  dependentOptions: DependentOptionResponseBody[] | null;
  onChange: EventActionResponseBody[] | null;
  onBlur: EventActionResponseBody[] | null;
  onFocus: EventActionResponseBody[] | null;
  showWhen: ShowWhenResponseBody | null;
};
export type DateRangeRendererResponseBody = {
  isReadOnly?: boolean;
  renderer: 'dateRange';
  name: string;
  label: string;
  tooltip?: string | null;
  columnSpan: Integer | null;
  rules?: InputRuleSet | null;
  onChange: EventActionResponseBody[] | null;
  onBlur: EventActionResponseBody[] | null;
  onFocus: EventActionResponseBody[] | null;
  showWhen: ShowWhenResponseBody | null;
};
export type DateRendererResponseBody = {
  isReadOnly?: boolean;
  renderer: 'date';
  name: string;
  label: string;
  tooltip?: string | null;
  columnSpan: Integer | null;
  rules?: InputRuleSet | null;
  onChange: EventActionResponseBody[] | null;
  onBlur: EventActionResponseBody[] | null;
  onFocus: EventActionResponseBody[] | null;
  showWhen: ShowWhenResponseBody | null;
};
export type DateTimeRendererResponseBody = {
  isReadOnly?: boolean;
  renderer: 'dateTime';
  name: string;
  label: string;
  tooltip?: string | null;
  columnSpan: Integer | null;
  rules?: InputRuleSet | null;
  onChange: EventActionResponseBody[] | null;
  onBlur: EventActionResponseBody[] | null;
  onFocus: EventActionResponseBody[] | null;
  showWhen: ShowWhenResponseBody | null;
};
export type DividerRendererResponseBody = {
  renderer: 'divider';
  name: string;
  columnSpan: Integer | null;
  orientation: ('horizontal' | 'vertical') | null;
  spacing: number | null;
};
export type EmailRendererResponseBody = {
  isReadOnly?: boolean;
  renderer: 'email';
  name: string;
  tooltip?: string | null;
  label: string;
  columnSpan: Integer | null;
  placeholder: string | null;
  rules?: InputRuleSet | null;
  onChange: EventActionResponseBody[] | null;
  onBlur: EventActionResponseBody[] | null;
  onFocus: EventActionResponseBody[] | null;
  showWhen: ShowWhenResponseBody | null;
};
export type NumberRendererResponseBody = {
  isReadOnly?: boolean;
  renderer: 'number';
  name: string;
  label: string;
  tooltip?: string | null;
  columnSpan: Integer | null;
  decimalPlaces?: Integer;
  rules?: InputRuleSet | null;
  onChange: EventActionResponseBody[] | null;
  onBlur: EventActionResponseBody[] | null;
  onFocus: EventActionResponseBody[] | null;
  showWhen: ShowWhenResponseBody | null;
};
export type MoneyRendererResponseBody = {
  isReadOnly?: boolean;
  renderer: 'money';
  name: string;
  label: string;
  tooltip?: string | null;
  columnSpan: Integer | null;
  decimalPlaces?: Integer;
  currency: CurrencyCodeType;
  rules?: InputRuleSet | null;
  onChange: EventActionResponseBody[] | null;
  onBlur: EventActionResponseBody[] | null;
  onFocus: EventActionResponseBody[] | null;
  showWhen: ShowWhenResponseBody | null;
};
export type PhoneRendererResponseBody = {
  isReadOnly?: boolean;
  renderer: 'phone';
  name: string;
  label: string;
  tooltip?: string | null;
  columnSpan: Integer | null;
  rules?: InputRuleSet | null;
  onChange: EventActionResponseBody[] | null;
  onBlur: EventActionResponseBody[] | null;
  onFocus: EventActionResponseBody[] | null;
  showWhen: ShowWhenResponseBody | null;
};
export type SelectRendererResponseBody = {
  isReadOnly?: boolean;
  defaultValue?: string | null;
  renderer: 'select';
  name: string;
  url: string | null;
  label: string;
  tooltip?: string | null;
  placeholder: string | null;
  isMulti: boolean | null;
  isClearable: boolean | null;
  columnSpan: Integer | null;
  rules?: InputRuleSet | null;
  defaultOptions: OptionResponseBody[];
  dependentOptions: DependentOptionResponseBody[] | null;
  onChange: EventActionResponseBody[] | null;
  onBlur: EventActionResponseBody[] | null;
  onFocus: EventActionResponseBody[] | null;
  showWhen: ShowWhenResponseBody | null;
};
export type SliderRendererResponseBody = {
  isReadOnly?: boolean;
  renderer: 'slider';
  name: string;
  label: string;
  tooltip?: string | null;
  min: number | null;
  max: number | null;
  step: number | null;
  columnSpan: Integer | null;
  isRange: boolean | null;
  rules?: InputRuleSet | null;
  onChange: EventActionResponseBody[] | null;
  onBlur: EventActionResponseBody[] | null;
  onFocus: EventActionResponseBody[] | null;
  showWhen: ShowWhenResponseBody | null;
};
export type SwitchRendererResponseBody = {
  isReadOnly?: boolean;
  renderer: 'switch';
  name: string;
  label: string;
  tooltip?: string | null;
  rules?: InputRuleSet | null;
  columnSpan: Integer | null;
  onChange: EventActionResponseBody[] | null;
  onBlur: EventActionResponseBody[] | null;
  onFocus: EventActionResponseBody[] | null;
  showWhen: ShowWhenResponseBody | null;
};
export type TextRendererResponseBody = {
  isReadOnly?: boolean;
  renderer: 'text';
  name: string;
  label: string;
  columnSpan: Integer | null;
  placeholder: string | null;
  tooltip?: string | null;
  rules?: InputRuleSet | null;
  onChange: EventActionResponseBody[] | null;
  onBlur: EventActionResponseBody[] | null;
  onFocus: EventActionResponseBody[] | null;
  showWhen: ShowWhenResponseBody | null;
};
export type PasswordRendererResponseBody = {
  isReadOnly?: boolean;
  renderer: 'password';
  name: string;
  label: string;
  columnSpan: Integer | null;
  tooltip?: string | null;
  rules?: InputRuleSet | null;
  onChange: EventActionResponseBody[] | null;
  onBlur: EventActionResponseBody[] | null;
  onFocus: EventActionResponseBody[] | null;
  showWhen: ShowWhenResponseBody | null;
};
export type RowRendererResponseBody = {
  isReadOnly?: boolean;
  renderer: 'row';
  name: string;
  columnSpan: Integer | null;
  columns: Integer;
  rendererRegistry: RendererRegistry;
  elements: (
    | CardRendererResponseBody
    | CheckboxesRendererResponseBody
    | CheckboxRendererResponseBody
    | ChipsRendererResponseBody
    | DateRangeRendererResponseBody
    | DateRendererResponseBody
    | DividerRendererResponseBody
    | EmailRendererResponseBody
    | NumberRendererResponseBody
    | PhoneRendererResponseBody
    | RowRendererResponseBody
    | SelectRendererResponseBody
    | SliderRendererResponseBody
    | SwitchRendererResponseBody
    | TextRendererResponseBody
    | PasswordRendererResponseBody
    | DateTimeRendererResponseBody
    | MoneyRendererResponseBody
  )[];
};

export type CardRendererResponseBody = {
  isReadOnly?: boolean;
  renderer: 'card';
  header: string | null;
  name: string;
  variant: ('default' | 'inlineGrey' | 'inlineWhite') | null;
  columnSpan: Integer | null;
  isExpandable: boolean | null;
  isClosedByDefault: boolean | null;
  rendererRegistry: RendererRegistry;
  elements: (
    | CardRendererResponseBody
    | CheckboxesRendererResponseBody
    | CheckboxRendererResponseBody
    | ChipsRendererResponseBody
    | DateRangeRendererResponseBody
    | DateRendererResponseBody
    | DividerRendererResponseBody
    | EmailRendererResponseBody
    | NumberRendererResponseBody
    | PhoneRendererResponseBody
    | RowRendererResponseBody
    | SelectRendererResponseBody
    | SliderRendererResponseBody
    | SwitchRendererResponseBody
    | TextRendererResponseBody
    | PasswordRendererResponseBody
    | DateTimeRendererResponseBody
    | MoneyRendererResponseBody
  )[];
};
